import React from 'react';
import { useProjects } from '../context/ProjectContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format, isBefore, addDays } from 'date-fns';

const StatCard = ({ title, value, icon, color, subtext }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-start justify-between">
    <div>
      <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-3xl font-bold dark:text-white">{value}</h3>
      {subtext && <p className="text-xs text-gray-400 mt-2">{subtext}</p>}
    </div>
    <div className={`p-3 rounded-xl ${color} bg-opacity-10 dark:bg-opacity-20`}>
      <SafeIcon icon={icon} className={`text-2xl ${color.replace('bg-', 'text-')}`} />
    </div>
  </div>
);

const Dashboard = () => {
  const { projects } = useProjects();
  const activeProjects = projects.filter(p => p.status !== 'Complete' && p.status !== 'Archived');
  const criticalProjects = activeProjects.filter(p => p.urgency === 'Critical');
  const upcomingDeadlines = activeProjects.filter(p => p.delivery_date && isBefore(new Date(p.delivery_date), addDays(new Date(), 7)));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Projects" value={activeProjects.length} icon={FiIcons.FiActivity} color="bg-blue-500" subtext={`${projects.filter(p => p.status === 'Editing').length} editing`} />
        <StatCard title="Critical Urgency" value={criticalProjects.length} icon={FiIcons.FiAlertCircle} color="bg-red-500" subtext="Requires immediate attention" />
        <StatCard title="Due This Week" value={upcomingDeadlines.length} icon={FiIcons.FiClock} color="bg-orange-500" subtext="Upcoming deliveries" />
        <StatCard title="Sent for Review" value={projects.filter(p => p.status === 'Sent for review').length} icon={FiIcons.FiCheckSquare} color="bg-green-500" subtext="Ready for review" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold mb-4 dark:text-white">Upcoming Deadlines</h2>
          <div className="space-y-4">
            {upcomingDeadlines.length > 0 ? upcomingDeadlines.map(project => (
              <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-12 rounded-full ${project.urgency === 'Critical' ? 'bg-red-500' : project.urgency === 'High' ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                  <div>
                    <h4 className="font-semibold dark:text-white">{project.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{project.stakeholder} • {project.department}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-bold text-indigo-600 dark:text-indigo-400">
                    {format(new Date(project.delivery_date), 'MMM d')}
                  </p>
                  <span className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border dark:border-gray-600 text-gray-600 dark:text-gray-300">
                    {project.status}
                  </span>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-8">No upcoming deadlines this week.</p>
            )}
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="font-bold text-lg mb-2">Creative Flow AI</h3>
            <p className="text-indigo-100 text-sm mb-4">Need help planning? Ask me to set up a new timeline.</p>
            <button className="w-full py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-sm font-medium transition"> Open Assistant </button>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-bold mb-4 dark:text-white">Department Load</h3>
            <div className="space-y-3">
              {['Marketing', 'Product', 'HR', 'Sales', 'Leadership', 'R&A', 'ExCo', 'Eco Teams', 'Internal Comms', 'External Comms'].map(dept => {
                const count = projects.filter(p => p.department === dept).length;
                const total = projects.length;
                const percentage = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={dept}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">{dept}</span>
                      <span className="font-medium dark:text-gray-400">{count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${percentage}%` }}></div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;